import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import { BenefitPeriod } from "certification/models/cti";
import { ToastController } from "ionic-angular";
import * as moment from "moment-timezone";
import { CurantisDatetimePipe } from "../../../pipes/output-formatters/curantis-datetime";
import { Hospice } from "../../models/hospice";
import {
  CertificationF2FNarrative,
  CertificationPeriod,
  CertificationService,
} from "../../services/certification-service";

@Component({
  selector: "initial-cti-form",
  templateUrl: "initial.cti.html",
})
export class InitialCtiForm implements AfterViewInit, OnInit {
  @Input() hospice: Hospice;
  @Input() isDeferred: boolean = false;
  @Input() patient: any = null;
  @Input() ctiv2: boolean;
  @Input()
  set useVerbalCti(value) {
    this.isVerbalCti = value;
    this.isWrittenCti = !this.isVerbalCti;
  }
  ctiBenefitPeriod: BenefitPeriod;
  @Input()
  set currentBenefitPeriod(benefitPeriod: BenefitPeriod) {
    this.ctiBenefitPeriod = benefitPeriod;
  }
  @Input()
  set useWrittenCti(value) {
    this.isWrittenCti = value;
    this.isVerbalCti = !this.isWrittenCti;
  }

  @Output()
  formValueChanged: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("attestation")
  private attestationEl: ElementRef;
  private isVerbalCti: boolean = false;
  private isWrittenCti: boolean = false;

  public certificationPeriodFrom;
  public certificationPeriodTo;
  public f2fNarrative: CertificationF2FNarrative;

  get useVerbalCti() {
    return this.isVerbalCti && this.isVerbalCti === true;
  }

  get useWrittenCti() {
    return this.isWrittenCti && this.isWrittenCti === true;
  }

  requiredIcon = faAsterisk;
  protected initialCtiForm: FormGroup;

  protected get patientForAttestation(): any {
    if (this.patient) {
      const { displayName, qbosId, lastName, firstName } = this.patient;
      return {
        name: displayName,
        id: qbosId,
        firstName,
        lastName,
        middleInitial: "q",
        certifiedDate: "01/01/2019",
      };
    }
    return null;
  }

  constructor(
    private formBuilder: FormBuilder,
    private certificationSvc: CertificationService,
    private toastCtrl: ToastController,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private elRef: ElementRef
  ) {
    this.hospice = null;
  }

  ngOnInit(): void {
    if (this.useWrittenCti) {
      this.initialCtiForm = this.formBuilder.group({
        narrativeStatement: [null, Validators.required],
        certificationPeriodFrom: ["", Validators.required],
        certificationPeriodTo: ["", Validators.required],
        signedBy: [
          "",
          Validators.compose([Validators.required, Validators.maxLength(150)]),
        ],
        signedByDate: [null],
      });
      //  force change detection after form creation as voice-ion-textarea record button is not initially rendered
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    }
    if (this.useVerbalCti) {
      this.initialCtiForm = this.formBuilder.group({
        obtainedFrom: [
          "",
          Validators.compose([Validators.required, Validators.maxLength(150)]),
        ],
        staffSignature: ["", [Validators.required, Validators.maxLength(150)]],
        certificationPeriodFrom: ["", Validators.required],
        certificationPeriodTo: ["", Validators.required],
        obtainFromDate: [null, Validators.required],
        staffSignatureDate: [null, Validators.required],
      });
    }
    this.initialCtiForm.valueChanges.subscribe((value) => {
      value.attestation = this.attestationEl.nativeElement.textContent;
      this.formValueChanged.emit(value);
    });

    if (this.ctiv2) {
      if (this.ctiBenefitPeriod != undefined) {
        let newBenefitPeriod: CertificationPeriod = {
          certificationFromDate: this.ctiBenefitPeriod.startDate,
          certificationToDate: this.ctiBenefitPeriod.endDate,
        };
        this.updateCertificationPeriod(newBenefitPeriod);
      }
    } else {
      this.certificationSvc
        .getCertificationPeriod(this.patient.qbosId)
        .subscribe(
          (period) => this.updateCertificationPeriod(period),
          (err) => {
            this.toastCtrl.create({
              message: "Unable to load certification period.",
              duration: 4000,
              position: "below",
              cssClass: "error",
            });
          }
        );
    }

    this.certificationSvc
      .getCertificationF2FNarrative(this.patient.qbosId)
      .subscribe(
        (narrative) => {
          this.f2fNarrative = narrative;
          this.cdr.detectChanges();
        },
        (err) => {
          this.toastCtrl.create({
            message: "Unable to load existing patient narrative.",
            duration: 4000,
            position: "below",
            cssClass: "error",
          });
        }
      );
  }

  ngAfterViewInit(): void {
    let formCssClass = null;
    if (this.useVerbalCti) {
      formCssClass = "verbal-cti";
    }
    if (this.useWrittenCti) {
      formCssClass = "written-cti";
    }
    this.renderer.addClass(this.elRef.nativeElement.parentNode, formCssClass);
    const initialDateValue = new CurantisDatetimePipe().transform(
      new Date().toLocaleString(),
      "dateonly"
    );
    this.initialCtiForm.patchValue({ signedByDate: initialDateValue });
  }

  private updateCertificationPeriod(period: CertificationPeriod) {
    this.certificationPeriodFrom = moment(period.certificationFromDate).format(
      "MM/DD/YYYY"
    );
    this.certificationPeriodTo = moment(period.certificationToDate).format(
      "MM/DD/YYYY"
    );

    this.initialCtiForm
      .get("certificationPeriodFrom")
      .setValue(moment(period.certificationFromDate).format("MM/DD/YYYY"));
    this.initialCtiForm
      .get("certificationPeriodTo")
      .setValue(moment(period.certificationToDate).format("MM/DD/YYYY"));
  }

  get needSignature() {
    return !this.isDeferred;
  }

  get isFormValid() {
    if (this.initialCtiForm) {
      return this.initialCtiForm.valid;
    }
    return false;
  }

  get isFormInvalid() {
    return !this.isFormValid;
  }

  get formValue() {
    return this.initialCtiForm.value;
  }
}



// WEBPACK FOOTER //
// ./src/certification/forms/initial.cti/initial.cti.ts